import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Grid,
  Popover,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore,
  ExpandLess,
  Dashboard,
  Home,
  Settings,
  People,
  BarChart,
  PieChart,
  Assessment,
  Folder,
  Description,
  Image,
  VideoLibrary,
  Map,
  LocationOn,
  Notifications,
  Email,
  CalendarToday,
  Schedule,
  Star,
  Favorite,
  Bookmark,
  ShoppingCart,
  Store,
  Business,
  School,
  LocalHospital,
  Security,
  Lock,
  Public,
  Cloud,
  Storage,
  Computer,
  Phone,
  Tablet,
  Menu as MenuIcon,
  List,
  GridView,
  ViewList,
  AccountCircle,
  Group,
  Person,
  AdminPanelSettings,
  Build,
  Engineering,
  Science,
  Sports,
  MusicNote,
  Movie,
  Games,
  Restaurant,
  Flight,
  DirectionsCar,
  Train,
  DirectionsBus,
} from '@mui/icons-material';
import { menusService, municipiosService } from '../services/api';
import { useAppContext } from '../contexts/ContextProvider';

interface MenuMunicipio {
  id: number;
  nome: string;
}

interface Menu {
  id: number;
  nome: string;
  icone: string;
  ordem: number;
  tipo: string;
  ativo: boolean;
  submenus?: Submenu[];
  municipios?: MenuMunicipio[];
}

interface Submenu {
  id: number;
  nome: string;
  ordem: number;
  dashboard_url: string;
  ativo: boolean;
  tipo_conteudo?: string;
  codigo_html?: string;
}

// Mapeamento de nomes de ícones para componentes
const iconMap: { [key: string]: React.ComponentType<any> } = {
  dashboard: Dashboard,
  home: Home,
  settings: Settings,
  people: People,
  barChart: BarChart,
  pieChart: PieChart,
  assessment: Assessment,
  folder: Folder,
  description: Description,
  image: Image,
  videoLibrary: VideoLibrary,
  map: Map,
  locationOn: LocationOn,
  notifications: Notifications,
  email: Email,
  calendarToday: CalendarToday,
  schedule: Schedule,
  star: Star,
  favorite: Favorite,
  bookmark: Bookmark,
  shoppingCart: ShoppingCart,
  store: Store,
  business: Business,
  school: School,
  localHospital: LocalHospital,
  security: Security,
  lock: Lock,
  public: Public,
  cloud: Cloud,
  storage: Storage,
  computer: Computer,
  phone: Phone,
  tablet: Tablet,
  menu: MenuIcon,
  list: List,
  gridView: GridView,
  viewList: ViewList,
  accountCircle: AccountCircle,
  group: Group,
  person: Person,
  adminPanelSettings: AdminPanelSettings,
  build: Build,
  engineering: Engineering,
  science: Science,
  sports: Sports,
  musicNote: MusicNote,
  movie: Movie,
  games: Games,
  restaurant: Restaurant,
  flight: Flight,
  directionsCar: DirectionsCar,
  train: Train,
  directionsBus: DirectionsBus,
};

// Lista de ícones disponíveis
const availableIcons = Object.keys(iconMap);

const MenusPage: React.FC = () => {
  const { refetch: refetchContext } = useAppContext();
  const [menus, setMenus] = useState<Menu[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSubmenuDialog, setOpenSubmenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [editingSubmenu, setEditingSubmenu] = useState<Submenu | null>(null);
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<number[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [iconPickerAnchor, setIconPickerAnchor] = useState<HTMLElement | null>(null);
  const [municipios, setMunicipios] = useState<any[]>([]);
  const [selectedMunicipios, setSelectedMunicipios] = useState<number[]>([]);
  const [filterMunicipioId, setFilterMunicipioId] = useState<number | ''>('');

  const [formData, setFormData] = useState({
    nome: '',
    icone: 'dashboard',
    ordem: 1,
    tipo: 'global',
  });

  const [submenuFormData, setSubmenuFormData] = useState({
    nome: '',
    ordem: 1,
    dashboard_url: '',
    tipo_conteudo: 'url',
    codigo_html: '',
  });

  useEffect(() => {
    loadMenus();
    loadMunicipios();
  }, []);

  const loadMenus = async () => {
    try {
      const data = await menusService.getMenus();
      setMenus(data);
    } catch (err) {
      setError('Erro ao carregar menus');
    }
  };

  const loadMunicipios = async () => {
    try {
      const data = await municipiosService.getMunicipios();
      setMunicipios(data);
    } catch (err) {
      console.error('Erro ao carregar municípios:', err);
    }
  };

  const handleOpenDialog = async (menu?: Menu) => {
    if (menu) {
      setEditingMenu(menu);
      setFormData({
        nome: menu.nome,
        icone: menu.icone,
        ordem: menu.ordem,
        tipo: menu.tipo,
      });
      // Carregar municípios associados se for compartilhado
      if (menu.tipo === 'compartilhado') {
        try {
          const municipiosAssociados = await menusService.getMunicipiosByMenu(menu.id);
          setSelectedMunicipios(municipiosAssociados.map((m: any) => m.id));
        } catch (err) {
          console.error('Erro ao carregar municípios associados:', err);
          setSelectedMunicipios([]);
        }
      } else {
        setSelectedMunicipios([]);
      }
    } else {
      setEditingMenu(null);
      setFormData({ nome: '', icone: 'dashboard', ordem: 1, tipo: 'global' });
      setSelectedMunicipios([]);
    }
    setOpenDialog(true);
  };

  const handleOpenSubmenuDialog = (menuId: number, submenu?: Submenu) => {
    setSelectedMenuId(menuId);
    if (submenu) {
      setEditingSubmenu(submenu);
      setSubmenuFormData({
        nome: submenu.nome,
        ordem: submenu.ordem,
        dashboard_url: submenu.dashboard_url,
        tipo_conteudo: submenu.tipo_conteudo || 'url',
        codigo_html: submenu.codigo_html || '',
      });
    } else {
      setEditingSubmenu(null);
      setSubmenuFormData({ nome: '', ordem: 1, dashboard_url: '', tipo_conteudo: 'url', codigo_html: '' });
    }
    setOpenSubmenuDialog(true);
  };

  const handleSaveMenu = async () => {
    try {
      if (editingMenu) {
        await menusService.updateMenu(editingMenu.id, formData);
        // Se for compartilhado, atualizar associações de municípios
        if (formData.tipo === 'compartilhado') {
          await menusService.associateMunicipiosToMenu(editingMenu.id, selectedMunicipios);
        }
        setSuccess('Menu atualizado com sucesso!');
      } else {
        const newMenu = await menusService.createMenu(formData);
        // Se for compartilhado, associar municípios
        if (formData.tipo === 'compartilhado' && selectedMunicipios.length > 0) {
          await menusService.associateMunicipiosToMenu(newMenu.id, selectedMunicipios);
        }
        setSuccess('Menu criado com sucesso!');
      }
      setOpenDialog(false);
      loadMenus();
      // Atualizar o contexto para refletir as mudanças no menu lateral
      await refetchContext();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar menu');
    }
  };

  const handleSaveSubmenu = async () => {
    if (!selectedMenuId) return;

    try {
      if (editingSubmenu) {
        await menusService.updateSubmenu(editingSubmenu.id, submenuFormData);
        setSuccess('Submenu atualizado com sucesso!');
      } else {
        await menusService.createSubmenu(selectedMenuId, submenuFormData);
        setSuccess('Submenu criado com sucesso!');
      }
      setOpenSubmenuDialog(false);
      loadMenus();
      // Atualizar o contexto para refletir as mudanças no menu lateral
      await refetchContext();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar submenu');
    }
  };

  const handleDeleteMenu = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este menu?')) return;

    try {
      await menusService.deleteMenu(id);
      setSuccess('Menu excluído com sucesso!');
      loadMenus();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir menu');
    }
  };

  const handleDeleteSubmenu = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este submenu?')) return;

    try {
      await menusService.deleteSubmenu(id);
      setSuccess('Submenu excluído com sucesso!');
      loadMenus();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir submenu');
    }
  };

  const toggleExpandMenu = (menuId: number) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const filteredMenus = filterMunicipioId
    ? menus.filter((menu) => {
        if (menu.tipo === 'global') return true;
        if (menu.tipo === 'compartilhado') {
          return menu.municipios?.some((m: MenuMunicipio) => m.id === filterMunicipioId);
        }
        return false;
      })
    : menus;

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Gerenciar Menus</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Novo Menu
        </Button>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Filtrar por Municipio</InputLabel>
          <Select
            value={filterMunicipioId}
            label="Filtrar por Municipio"
            onChange={(e) => setFilterMunicipioId(e.target.value as number | '')}
          >
            <MenuItem value="">Todos os municipios</MenuItem>
            {municipios.map((mun) => (
              <MenuItem key={mun.id} value={mun.id}>{mun.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filteredMenus.length} menu{filteredMenus.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50"></TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Municipios</TableCell>
              <TableCell>Ordem</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMenus.map((menu) => (
              <React.Fragment key={menu.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpandMenu(menu.id)}
                    >
                      {expandedMenus.includes(menu.id) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{menu.nome}</TableCell>
                  <TableCell>
                    <Chip
                      label={menu.tipo}
                      size="small"
                      color={menu.tipo === 'global' ? 'primary' : menu.tipo === 'compartilhado' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {menu.tipo === 'global' ? (
                      <Typography variant="caption" color="text.secondary">Todos</Typography>
                    ) : menu.municipios && menu.municipios.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {menu.municipios.map((mun: MenuMunicipio) => (
                          <Chip key={mun.id} label={mun.nome} size="small" variant="outlined" color="info" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="error">Nenhum</Typography>
                    )}
                  </TableCell>
                  <TableCell>{menu.ordem}</TableCell>
                  <TableCell>
                    <Chip
                      label={menu.ativo ? 'Ativo' : 'Inativo'}
                      size="small"
                      color={menu.ativo ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(menu)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteMenu(menu.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenSubmenuDialog(menu.id)}
                    >
                      Submenu
                    </Button>
                  </TableCell>
                </TableRow>
                {expandedMenus.includes(menu.id) && menu.submenus && menu.submenus.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width="50"></TableCell>
                            <TableCell>Nome do Submenu</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Conteúdo</TableCell>
                            <TableCell>Ordem</TableCell>
                            <TableCell align="right">Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {menu.submenus.map((submenu) => (
                            <TableRow key={submenu.id}>
                              <TableCell></TableCell>
                              <TableCell>{submenu.nome}</TableCell>
                              <TableCell>
                                <Chip
                                  label={submenu.tipo_conteudo === 'html' ? 'HTML' : 'URL'}
                                  size="small"
                                  color={submenu.tipo_conteudo === 'html' ? 'secondary' : 'primary'}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {submenu.tipo_conteudo === 'html' 
                                    ? 'Código incorporado' 
                                    : submenu.dashboard_url}
                                </Typography>
                              </TableCell>
                              <TableCell>{submenu.ordem}</TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenSubmenuDialog(menu.id, submenu)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteSubmenu(submenu.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Menu */}
      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          setIconPickerAnchor(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{editingMenu ? 'Editar Menu' : 'Novo Menu'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nome"
              fullWidth
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Ícone
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={(e) => setIconPickerAnchor(e.currentTarget)}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  height: 56,
                  borderColor: iconPickerAnchor ? 'primary.main' : 'divider',
                }}
              >
                {formData.icone && iconMap[formData.icone] ? (
                  <>
                    {React.createElement(iconMap[formData.icone], { sx: { mr: 2 } })}
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {formData.icone}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Selecione um ícone
                  </Typography>
                )}
              </Button>
            </Box>
            <TextField
              label="Ordem"
              type="number"
              fullWidth
              value={formData.ordem}
              onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) })}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.tipo}
                label="Tipo"
                onChange={(e) => {
                  const novoTipo = e.target.value;
                  setFormData({ ...formData, tipo: novoTipo });
                  // Limpar seleção de municípios se mudar para global ou privado
                  if (novoTipo !== 'compartilhado') {
                    setSelectedMunicipios([]);
                  }
                }}
              >
                <MenuItem value="global">Global (todos os municípios)</MenuItem>
                <MenuItem value="compartilhado">Compartilhado (municípios selecionados)</MenuItem>
                <MenuItem value="privado">Privado (usuários específicos)</MenuItem>
              </Select>
            </FormControl>

            {formData.tipo === 'compartilhado' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Municípios com acesso
                </Typography>
                <Autocomplete
                  multiple
                  options={municipios}
                  getOptionLabel={(option) => `${option.nome} (IBGE: ${option.codigo_ibge})`}
                  value={municipios.filter((m) => selectedMunicipios.includes(m.id))}
                  onChange={(_, newValue) => {
                    setSelectedMunicipios(newValue.map((m) => m.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecione os municípios"
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} />
                      {option.nome} (IBGE: {option.codigo_ibge})
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={`${option.nome} (${option.codigo_ibge})`}
                        size="small"
                      />
                    ))
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Selecione quais municípios terão acesso a este menu
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveMenu} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popover Seletor de Ícones */}
      <Popover
        open={Boolean(iconPickerAnchor)}
        anchorEl={iconPickerAnchor}
        onClose={() => setIconPickerAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            p: 2,
          },
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Selecione um Ícone
        </Typography>
        <Box
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          <Grid container spacing={1}>
            {availableIcons.map((iconName) => {
              const IconComponent = iconMap[iconName];
              const isSelected = formData.icone === iconName;
              return (
                <Grid item xs={3} key={iconName}>
                  <Box
                    onClick={() => {
                      setFormData({ ...formData, icone: iconName });
                      setIconPickerAnchor(null);
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      cursor: 'pointer',
                      borderRadius: 1,
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.light' : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.light' : 'action.hover',
                        borderColor: 'primary.main',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    {React.createElement(IconComponent, {
                      sx: { fontSize: 32, color: isSelected ? 'primary.main' : 'text.primary' },
                    })}
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        fontSize: '0.7rem',
                        textAlign: 'center',
                        textTransform: 'capitalize',
                        color: isSelected ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {iconName}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Popover>

      {/* Dialog Submenu */}
      <Dialog
        open={openSubmenuDialog}
        onClose={() => setOpenSubmenuDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingSubmenu ? 'Editar Submenu' : 'Novo Submenu'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nome"
              fullWidth
              value={submenuFormData.nome}
              onChange={(e) =>
                setSubmenuFormData({ ...submenuFormData, nome: e.target.value })
              }
            />
            
            <FormControl fullWidth>
              <InputLabel>Tipo de Conteúdo</InputLabel>
              <Select
                value={submenuFormData.tipo_conteudo}
                label="Tipo de Conteúdo"
                onChange={(e) =>
                  setSubmenuFormData({ ...submenuFormData, tipo_conteudo: e.target.value })
                }
              >
                <MenuItem value="url">URL (Link Direto)</MenuItem>
                <MenuItem value="html">Código HTML (Tableau, PowerBI, etc)</MenuItem>
              </Select>
            </FormControl>

            {submenuFormData.tipo_conteudo === 'url' && (
              <TextField
                label="URL do Dashboard"
                fullWidth
                value={submenuFormData.dashboard_url}
                onChange={(e) =>
                  setSubmenuFormData({ ...submenuFormData, dashboard_url: e.target.value })
                }
                placeholder="https://example.com/embed/dashboard"
              />
            )}

            {submenuFormData.tipo_conteudo === 'html' && (
              <TextField
                label="Código HTML de Incorporação"
                fullWidth
                multiline
                rows={8}
                value={submenuFormData.codigo_html}
                onChange={(e) =>
                  setSubmenuFormData({ ...submenuFormData, codigo_html: e.target.value })
                }
                placeholder='<div class="tableauPlaceholder">...</div><script>...</script>'
                helperText="Cole aqui o código completo fornecido pelo Tableau, PowerBI ou outra plataforma"
              />
            )}
            
            <TextField
              label="Ordem"
              type="number"
              fullWidth
              value={submenuFormData.ordem}
              onChange={(e) =>
                setSubmenuFormData({ ...submenuFormData, ordem: parseInt(e.target.value) })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubmenuDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveSubmenu} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MenusPage;
